<template>
  <div v-if="getDetailCourse" class="min-h-screen shadow-lg">
    <BlNavBarMv
      class="sticky top-0 z-10"
      title="Kartu Prakerja"
      style="border-bottom: 1px solid #ebebeb"
      has-back-button
      @handleBackButtonClick="onClickBack"
    />
    <div class="prakerja-checkout">
      <PrakerjaCheckoutSelectedVoucher
        :course-type="courseType"
        :name="getDetailCourse.name"
        :illustration-url="optimizeImageWidth(getDetailCourse.images[0].url, 300)"
      />

      <div class="bg-ui-light p-4 border-t border-0 border-gray-5 border-solid" />

      <PrakerjaCheckoutInquiryForm
        :error-message="!sheetConfig.isErrorWithCtaActive ? inquiryFormErrorMessage : ''"
        @onRetrievePayload="retrievePayload"
      />

      <div class="bg-ui-light p-4 border-t border-0 border-gray-5 border-solid" />

      <template v-if="courseSchedule.schedule_name">
        <div class="ph-20 mv-12">
          <BlTextAv class="mb-12" variation="body-16" weight="medium">Jadwal Kelas</BlTextAv>
          <BlTextAv class="qa-txt-schedule-name">{{ courseSchedule.schedule_name }}</BlTextAv>

          <BlFlexContainerMv v-if="!getIsSelfPaced" direction="row" align-items="flex-start" style="overflow-x: hidden">
            <BlFlexItemAv>
              <IcoTimeMinor class="mv-16" color="#B6B6B6" />
            </BlFlexItemAv>
            <BlFlexItemAv style="width: 100%">
              <BlTextAv variation="body-14" class="pt-16 pl-4 mb-12" weight="bold">Waktu</BlTextAv>
              <template v-if="courseSchedule.times.length === 1">
                <BlTextAv class="pl-4 qa-txt-time" variation="body-14">
                  {{ courseSchedule.times[0].schedule_date | humanizeTimestamp }}, {{ courseSchedule.times[0].start_time }}-{{
                    courseSchedule.times[0].end_time
                  }}
                  {{ courseSchedule.times[0].timezone.toUpperCase() }}
                </BlTextAv>
              </template>
              <template v-else>
                <ul class="pl-28 m-0">
                  <li v-for="(t, j) in courseSchedule.times" :key="j">
                    <BlTextAv class="qa-txt-times" variation="body-14">
                      {{ t.schedule_date | humanizeTimestamp }}, {{ t.start_time }}-{{ t.end_time }} {{ t.timezone.toUpperCase() }}
                    </BlTextAv>
                  </li>
                </ul>
              </template>
            </BlFlexItemAv>
          </BlFlexContainerMv>
          <BlFlexContainerMv v-if="!getIsSelfPaced" direction="row" align-items="flex-start" style="overflow-x: hidden">
            <BlFlexItemAv>
              <IcoLocationMinor class="mv-16" color="#B6B6B6" />
            </BlFlexItemAv>
            <BlFlexItemAv style="width: 100%">
              <BlTextAv class="pt-16 pl-4 mb-12" variation="body-14" weight="bold">Lokasi</BlTextAv>
              <BlTextAv class="pl-4 qa-txt-location" variation="body-14">
                {{ courseSchedule.address }}{{ courseType !== 'webinar' ? `, ${courseSchedule.city}, ${courseSchedule.province}` : '' }}
              </BlTextAv>
            </BlFlexItemAv>
          </BlFlexContainerMv>
          <BlFlexContainerMv direction="row" align-items="flex-start" style="overflow-x: hidden">
            <BlFlexItemAv>
              <IcoAccountBoxMinor class="mv-16" color="#B6B6B6" />
            </BlFlexItemAv>
            <BlFlexItemAv style="width: 100%">
              <BlTextAv class="pt-16 pl-4 mb-12" variation="body-14" weight="bold">Instruktur</BlTextAv>
              <BlTextAv class="pl-4 qa-txt-trainer" variation="body-14">{{ courseSchedule.trainer }}</BlTextAv>
            </BlFlexItemAv>
          </BlFlexContainerMv>
          <BlFlexContainerMv v-if="!getIsSelfPaced" direction="row" align-items="flex-start" style="overflow-x: hidden">
            <BlFlexItemAv>
              <IcoCorporate class="mv-16" color="#B6B6B6" style="width: 90%" />
            </BlFlexItemAv>
            <BlFlexItemAv style="width: 100%">
              <BlTextAv class="pt-16 pl-4 mb-12" variation="body-14" weight="bold">Kapasitas</BlTextAv>
              <BlTextAv class="pl-4 qa-txt-capacity" variation="body-14">
                {{ courseSchedule.capacity }} Orang
                <template v-if="getCheckQuotaToggle && quotas.length > 0">
                  {{ quotas[0].remaining_capacity > 0 ? `(sisa ${quotas[0].remaining_capacity} slot)` : '(slot penuh)' }}
                </template>
              </BlTextAv>
            </BlFlexItemAv>
          </BlFlexContainerMv>
        </div>
        <div class="bg-ui-light p-4 border-t border-0 border-gray-5 border-solid" />
      </template>

      <PrakerjaCheckoutCostInformation
        :price="getDetailCourse.discount.original_price"
        :cta-enabled="isPrakerjaNumberValid"
        @click="openPaymentConfirmation"
      />
    </div>

    <BlSheetMv :active.sync="sheetConfig.isPaymentConfirmationActive" class="prakerja-checkout-form__sheet" title="" auto-height>
      <div slot="content" style="padding: 16px">
        <BlTextAv variation="heading-24">Pakai saldo kartu?</BlTextAv>
        <BlTextAv variation="body-14" class="mt-12 mb-24 block">
          Saldo Kartu Prakerja kamu akan berkurang sesuai total pembayaran buat ikut kelas yang dipilih.
        </BlTextAv>
        <BlButtonAv variation="primary" class="checkout-form__trigger-otp" fullwidth @click="triggerOtp">
          <template v-if="isTrigeringOtp">
            <BlSpinnerAv :is-loading="isTrigeringOtp" is-small />
          </template>
          <template v-else>Ya, lanjutkan pembayaran</template>
        </BlButtonAv>
        <BlButtonAv variation="secondary" fullwidth class="mt-12" @click="sheetConfig.isPaymentConfirmationActive = false">
          Periksa lagi
        </BlButtonAv>
      </div>
    </BlSheetMv>

    <CustomBlSheetMv
      :active.sync="sheetConfig.isNewPaymentConfirmationActive"
      class="new-prakerja-checkout-form__sheet"
      title="Konfirmasi Pembayaran"
      auto-height
      @close="sheetConfig.isNewPaymentConfirmationActive = false"
    >
      <div slot="content" style="padding: 16px">
        <BlTextAv variation="body-16" weight="bold">Agar transaksi sukses, perhatikan dulu ya:</BlTextAv>
        <div class="mt-12">
          <BlFlexContainerMv align-items="center">
            <BlIllustrationAv name="img_small_account_user" class="new-prakerja-checkout-form-image-item" />
            <BlTextAv class="ml-16" variation="body-14">1 akun Bukalapak hanya bisa pakai 1 Kartu Prakerja untuk pembelian kelas.</BlTextAv>
          </BlFlexContainerMv>
          <BlFlexContainerMv align-items="center">
            <BlIllustrationAv name="img_small_money_dompet" class="new-prakerja-checkout-form-image-item" />
            <BlTextAv class="ml-16" variation="body-14">
              Saldo Kartu Prakerja kamu akan berkurang sesuai total pembayaran kelas setelah transaksi sukses.
            </BlTextAv>
          </BlFlexContainerMv>
        </div>
        <BlButtonAv variation="primary" class="checkout-form__trigger-otp" fullwidth @click="triggerOtp">
          <template v-if="isTrigeringOtp">
            <BlSpinnerAv :is-loading="isTrigeringOtp" is-small />
          </template>
          <template v-else>Ya, lanjut bayar</template>
        </BlButtonAv>
        <BlButtonAv variation="outline" fullwidth class="mt-12" @click="sheetConfig.isNewPaymentConfirmationActive = false">
          Cek lagi nomor kartu
        </BlButtonAv>
      </div>
    </CustomBlSheetMv>

    <BlSheetMv
      :active.sync="sheetConfig.isErrorWithCtaActive"
      class="qa-sheet-error-message"
      title=""
      auto-height
      @close="handleChangeSchedule"
    >
      <i slot="iconLeftNavBar"></i>
      <div slot="content" style="padding: 16px">
        <center>
          <BlIllustrationAv :alt="error.message.title" class="prakerja-checkout-form__error-message" name="img_medium_fun_search_fail" />
          <BlTextAv class="qa-txt-title-error" variation="subheading-18">{{ error.message.title }}</BlTextAv>
          <BlTextAv class="qa-txt-subtitle-error mt-12 mb-20 block" variation="body-14">
            {{ error.message.content }}
          </BlTextAv>
          <BlButtonAv data-testid="handle-change-schedule-cta" class="mt-12" fullwidth @click="handleChangeSchedule">
            {{ ctaLabel }}
          </BlButtonAv>
        </center>
      </div>
    </BlSheetMv>
  </div>
</template>

<script>
import {humanizeTimestamp} from '@bukalapak/toolbox-helper/date-helper';
import {mapGetters} from 'vuex';
import {BlSpinnerAv, BlSheetMv, BlButtonAv, BlNavBarMv, BlFlexContainerMv, BlFlexItemAv} from '@bukalapak/bazaar-mweb-latest';
import CustomBlSheetMv from '../components/temporary-bazaar-components/BlSheetMv/BlSheetMv.vue';
import {IcoTimeMinor, IcoLocationMinor, IcoAccountBoxMinor, IcoCorporate, BlIllustrationAv} from '@bukalapak/bazaar-visual-next';
import {optimizeImageWidth} from 'shared_js/image-helpers';
import {TRACKER_EVENT_VP_PRAKERJA_CONFIRMATION, TRACKER_EVENT_VP_PRAKERJA_SUCCESS} from '../config/tracker-events';
import PrakerjaCheckoutSelectedVoucher from '../components/PrakerjaCheckout/PrakerjaCheckoutSelectedVoucher';
import PrakerjaCheckoutInquiryForm from '../components/PrakerjaCheckout/PrakerjaCheckoutInquiryForm';
import PrakerjaCheckoutCostInformation from '../components/PrakerjaCheckout/PrakerjaCheckoutCostInformation';
import trackerGenerator from '../mixins/tracker-generator';
import Braze from 'shared_js/helpers/braze';

const VOUCHER_NOT_AVAILABLE_ERROR_CODE = 'VOUCHERNOTAVAILABLE';
const CHANGE_CARD_NUMBER_ERROR_CODES = ['ERRUSR7038', 'ERRUSR7039'];

const ERROR_MESSAGES = {
  ERR40015: {
    title: 'Tidak bisa ikut kelas yang dipilih',
    content: 'Maaf, kamu belum memenuhi ketentuan menjadi peserta kelas ini. Coba cari lagi, yuk!',
  },
  ERR40016: {
    title: 'Tidak bisa ikut kelas yang dipilih',
    content: 'Maaf, kamu belum memenuhi ketentuan menjadi peserta kelas ini. Coba cari lagi, yuk!',
  },
  ERRPAP40015: {
    title: 'Tidak bisa ikut kelas yang dipilih',
    content: 'Maaf, kamu belum memenuhi ketentuan menjadi peserta kelas ini. Coba cari lagi, yuk!',
  },
  ERRPAP40016: {
    title: 'Tidak bisa ikut kelas yang dipilih',
    content: 'Maaf, kamu belum memenuhi ketentuan menjadi peserta kelas ini. Coba cari lagi, yuk!',
  },
  ERRPLT40015: {
    title: 'Tidak bisa ikut kelas yang dipilih',
    content: 'Maaf, kamu belum memenuhi syarat usia untuk kelas ini. Coba cari lagi, yuk!',
  },
  ERRPLT40016: {
    title: 'Tidak bisa ikut kelas yang dipilih',
    content: 'Maaf, kamu belum memenuhi syarat pendidikan untuk kelas ini. Coba cari lagi, yuk!',
  },
  ERR40017: {
    title: 'Melewati waktu pembelian kelas',
    content: 'Pembelian hanya bisa dilakukan paling lambat 15 hari setelah penerimaan peserta Prakerja.',
  },
  ERR40020: {
    title: 'Melewati waktu pembelian kelas',
    content: 'Pembelian hanya bisa dilakukan paling lambat 15 hari setelah penerimaan peserta Prakerja.',
  },
  ERRUSR7026: {
    title: 'Melewati waktu pembelian kelas',
    content: 'Pembelian hanya bisa dilakukan paling lambat 15 hari setelah penerimaan peserta Prakerja.',
  },
  ERRPAP40035: {
    title: 'Melewati waktu pembelian kelas',
    content: 'Pembelian hanya bisa dilakukan paling lambat 15 hari setelah penerimaan peserta Prakerja.',
  },
  ERRPAP4001: {
    title: 'Melewati waktu pembelian kelas',
    content: 'Pembelian hanya bisa dilakukan paling lambat 15 hari setelah penerimaan peserta Prakerja.',
  },
  ERRTRX40046: {
    title: 'Melewati waktu pembelian kelas',
    content: 'Pembelian hanya bisa dilakukan paling lambat 15 hari setelah penerimaan peserta Prakerja.',
    ctaLabel: 'Kembali ke halaman depan',
  },
  HARDCODEDFULLSTOCK: {
    title: 'Maaf, kuota kelas sudah penuh',
    content: 'Tidak ada pemotongan saldo Kartu Prakerja. Coba cari lagi kelas yang lainnya, yuk!',
  },
  [VOUCHER_NOT_AVAILABLE_ERROR_CODE]: {
    title: 'Maaf, transaksi belum bisa diproses',
    content: 'Jadwal yang kamu pilih sedang tidak tersedia. Silakan coba lagi nanti atau pilih jadwal lain.',
  },
  ERRPLT40012: {
    title: 'Melewati batas pengiriman kode verifikasi',
    content: 'Kamu perlu tunggu maks. 15 menit untuk dapat kode verifikasi kartu Prakerja.',
    ctaLabel: 'Kembali ke halaman depan',
  },
  ERRPPA40025: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Kelas yang kamu pilih saat ini tidak tersedia. Coba cari kelas lain, yuk!',
  },
  ERRTRX40049: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Kamu perlu selesaikan dulu pelatihan yang sedang kamu ikuti agar bisa ikut kelas baru.',
    ctaLabel: 'Kembali ke halaman depan',
  },
  ERRPLT40022: {
    title: 'Melewati batas pengiriman kode verifikasi',
    content: 'Kamu perlu tunggu maks. 1 jam untuk dapat kode verifikasi kartu Prakerja.',
    ctaLabel: 'Kembali ke halaman depan',
  },
  ERRPLT40037: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Saat ini pembelian kelas yang kamu pilih tidak bisa diproses. Coba cari kelas lain, yuk!',
  },
  ERRUSR404: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Nomor Kartu Prakerja kamu tidak terdaftar. Kamu perlu pakai kartu yang aktif untuk beli kelas.',
    ctaLabel: 'Kembali ke halaman depan',
  },
  ERRTRX40048: {
    title: 'Tidak bisa ikut kelas yang dipilih',
    content: 'Kamu sudah pernah ikut kelas ini sebelumnya. Coba cari kelas lain, yuk!',
  },
  ERRTRX18790: {
    title: 'Melewati waktu pembelian kelas',
    content: 'Pembelian hanya bisa dilakukan setelah memberi ulasan kelas sebelumnya dalam 15 hari setelah kelas dimulai.',
    ctaLabel: 'Kembali ke halaman depan',
  },
  ERRSCH4006: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Saat ini pembelian kelas yang kamu pilih tidak bisa diproses. Coba cari kelas lain, yuk!',
  },
  ERRTRX40050: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Kamu perlu kasih rating dan ulasan dulu untuk pelatihan sebelumnya agar bisa ikut kelas baru.',
    ctaLabel: 'Kembali ke halaman depan',
  },
  ERRTRX40041: {
    title: 'Maaf, kuota kelas sudah penuh',
    content: 'Slot terakhir kelas ini sudah diisi peserta lain. Coba cari lagi kelas lainnya, yuk!',
  },
  ERRPLT4002: {
    title: 'Maaf, kuota kelas sudah penuh',
    content: 'Slot terakhir kelas ini sudah diisi peserta lain. Coba cari lagi kelas lainnya, yuk!',
  },
  ERRTRX40020: {
    title: 'Melewati waktu pembelian kelas',
    content: 'Kelas atau jadwal yang kamu pilih sudah tidak bisa dibeli. Coba cari kelas lain, yuk!',
  },
  ERRPLT40013: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Pembelian kelas yang kamu pilih saat ini tidak bisa diproses. Coba cari kelas lain, yuk!',
  },
  ERRTRX40044: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Kartu Prakerja kamu tidak bisa dipakai untuk ikut kelas. Hubungi CS Prakerja untuk info lebih lanjut.',
    ctaLabel: 'Kembali ke halaman depan',
  },
  ERRTRX40040: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Kamu perlu sambungkan rekening atau dompet digital ke akun Prakerja dulu untuk beli kelas.',
    ctaLabel: 'Kembali ke halaman depan',
  },
  ERRTRX40042: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Layanan pembelian kelas saat ini sedang ditutup. Coba lagi nanti, ya.',
    ctaLabel: 'Kembali ke halaman depan',
  },
  ERRTRX40043: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Saldo Kartu Prakerja kamu tidak cukup. Cek lagi saldo kamu atau coba cari kelas lain.',
  },
  ERRTRX40047: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Saldo Kartu Prakerja kamu tidak cukup untuk beli kelas yang dipilih. Coba cari kelas lain, yuk!',
  },
  ERRPPA40005: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Kelas yang kamu pilih saat ini tidak tersedia. Coba cari kelas lain, yuk!',
  },
  ERRPLT40035: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Saat ini jadwal pelatihan belum aktif. Silakan coba lagi nanti.',
    ctaLabel: 'Kembali ke halaman depan',
  },
  ERRPLT40036: {
    title: 'Maaf, transaksi tidak bisa diproses',
    content: 'Saat ini pembelian kelas yang kamu pilih tidak bisa diproses. Coba cari kelas lain, yuk!',
  },
  ERRUSR7040: {
    title: 'Maaf, ada gangguan sistem',
    content: 'Transaksi belum bisa kami proses. Kamu bisa coba lagi setelah beberapa saat.',
    ctaLabel: 'Kembali ke halaman depan',
  },
  ERRUSR7038: {
    title: '1 akun hanya untuk 1 Kartu Prakerja',
    content:
      'Kamu pernah transaksi pakai nomor Kartu Prakerja yang berbeda di akun Bukalapak ini. Silahkan transaksi lagi pakai nomor tersebut.',
    ctaLabel: 'Ubah nomor Kartu Prakerja',
  },
  ERRUSR7039: {
    title: 'Kartu Prakerja dipakai di akun lain',
    content:
      'Nomor Kartu Prakerja yang sama udah pernah kamu pakai transaksi di akun Bukalapak lain. Silahkan lanjut transaksi di akun tersebut, ya.',
    ctaLabel: 'Ubah nomor Kartu Prakerja',
  },
};

export default {
  name: 'CheckoutFormPage',
  components: {
    BlNavBarMv,
    BlSpinnerAv,
    BlSheetMv,
    BlButtonAv,
    BlIllustrationAv,
    IcoTimeMinor,
    IcoLocationMinor,
    IcoAccountBoxMinor,
    IcoCorporate,
    PrakerjaCheckoutSelectedVoucher,
    PrakerjaCheckoutInquiryForm,
    PrakerjaCheckoutCostInformation,
    CustomBlSheetMv,
    BlFlexContainerMv,
    BlFlexItemAv,
  },
  filters: {humanizeTimestamp},
  mixins: [trackerGenerator],
  data() {
    return {
      prevRoute: null,
      isTrigeringOtp: false,
      pmoErrorCodeMessage: ERROR_MESSAGES,
      error: {
        code: '',
        pmoCode: '',
        message: '',
      },
      sheetConfig: {
        isPaymentConfirmationActive: false,
        isNewPaymentConfirmationActive: false,
        isErrorWithCtaActive: false,
      },
      payload: {
        sku_id: '',
        prakerja_number: '',
      },
      quotas: [],
    };
  },
  computed: {
    ...mapGetters(['getPreviousPage', 'getCheckQuotaToggle', 'getNewPaymentPolicyToggle']),
    ...mapGetters('courses', ['getDetailCourse', 'getIsSelfPaced']),
    isPrakerjaNumberValid() {
      return this.payload.prakerja_number.length === 16;
    },
    courseSchedule() {
      if (this.getDetailCourse.course_schedules) {
        return this.getDetailCourse.course_schedules.find((cs) => cs.schedule_code === this.$route.query.schedule_code) || {};
      }
      return {};
    },
    courseType() {
      return this.getDetailCourse.course_type;
    },
    isVoucherNotAvailable() {
      return this.error.pmoCode === VOUCHER_NOT_AVAILABLE_ERROR_CODE;
    },
    ctaLabel() {
      const ctaLabel = this.error.message.ctaLabel;
      if (ctaLabel) return ctaLabel;

      return this.isVoucherNotAvailable ? 'Pilih jadwal lain' : 'Cari kelas lain';
    },
    inquiryFormErrorMessage() {
      if (typeof this.error.message === 'object') return `${this.error.message.title}. ${this.error.message.content}`;

      return this.error.message.replace('|', '. ');
    },
  },
  beforeMount() {
    this.$root.prakerjaToken = '';
    this.$root.phoneNumber = '';
    this.init();
  },
  mounted() {
    if (this.getCheckQuotaToggle) {
      this.$api
        .getCapacity({
          id: this.$route.query.course_id,
          schedule_code: this.$route.query.schedule_code,
        })
        .then(({data}) => {
          this.quotas = data;
        })
        .catch(() => {
          this.quotas = [];
        })
        .finally(() => {
          if (this.quotas?.[0]?.remaining_capacity <= 0) {
            this.setErrorResponse({
              errors: [
                {
                  message: '',
                  code: '',
                  original_error_code: 'HARDCODEDFULLSTOCK',
                },
              ],
              meta: {http_status: 422},
            });
          }
        });
    }
  },
  methods: {
    optimizeImageWidth,
    onClickBack() {
      if (this.getPreviousPage?.name) this.$router.go(-1);
      else this.$router.push({name: 'landing'});
    },
    openPaymentConfirmation() {
      if (this.getNewPaymentPolicyToggle) {
        this.sheetConfig.isNewPaymentConfirmationActive = true;
      } else {
        this.sheetConfig.isPaymentConfirmationActive = true;
      }
    },
    async init() {
      if (!this.$route.query.course_id || !this.$route.query.schedule_code) {
        this.$router.push({name: 'landing', query: this.$route.query});
      } else {
        const courseDetail = await this.$store.dispatch('courses/fetchCourseDetail', this.$route.query.course_id);
        if (!courseDetail) {
          this.$router.push({name: 'landing', query: this.$route.query});
        }
      }
      this.trackCheckoutVisit();
    },
    retrievePayload(form) {
      if (form) {
        this.payload.prakerja_number = form.prakerjaNumber.replace(/\s/g, '');

        this.payload.sku_id = this.getDetailCourse.id;
        this.clearErrorResponse();
      }
    },
    async triggerOtp() {
      this.sheetConfig.isPaymentConfirmationActive = false;

      if (this.isTrigeringOtp) return;

      try {
        this.isTrigeringOtp = true;

        // testing purposes. eg: preproduction300 -> 300
        const APP_ENV = process.env.APP_ENV;
        if (APP_ENV === 'preproduction' || APP_ENV === 'development') {
          this.payload.prakerja_number = this.payload.prakerja_number.replace('preproduction', '');
        }

        let otpTransactionPayload = {...this.payload, course_schedule_id: this.courseSchedule.id};
        const {data} = await this.$api.postOtpTransaction(otpTransactionPayload);

        this.$root.prakerjaToken = data.token;
        this.$root.phoneNumber = data.phone_number;
        this.$root.transactionId = data.id;

        this.clearErrorResponse();

        const trackerConfirmationPromise = this.trackPrakerjaConfirmation(true);
        const trackerSuccessPromise = this.trackPrakerjaSuccess();

        // wait for all tracker to be sent
        // ignore errors if any occurs during sending trackers attempt
        await Promise.all([trackerSuccessPromise, trackerConfirmationPromise].map((p) => p.catch((e) => e)));
        this.$router.replace({name: 'account-verification', query: this.$route.query});
      } catch (err) {
        await this.trackPrakerjaConfirmation(false);
        this.setErrorResponse(err);
        this.sheetConfig.isPaymentConfirmationActive = false;
        this.sheetConfig.isNewPaymentConfirmationActive = false;
        this.$logger.log('PrakerjaCheckoutFormPage - triggerOtp', err);
      }
      this.isTrigeringOtp = false;
    },
    setErrorResponse({errors} = {}) {
      this.error.message = errors && errors.length ? errors[0].message : 'Terjadi gangguan. Silakan coba lagi nanti.';
      this.error.code = errors && errors.length ? `${errors[0].code}` : '';
      this.error.pmoCode = errors && errors.length && errors[0].original_error_code ? `${errors[0].original_error_code}` : '';
      if (Object.keys(this.pmoErrorCodeMessage).includes(this.error.pmoCode)) {
        this.error.message = this.pmoErrorCodeMessage[this.error.pmoCode];
        this.sheetConfig.isErrorWithCtaActive = true;
      }
    },
    clearErrorResponse() {
      this.error.message = '';
      this.error.code = '';
      this.error.pmoCode = '';
    },
    trackPrakerjaConfirmation(isSuccess) {
      const payload = {
        success_prakerja_count: isSuccess ? 1 : 0,
        failed_prakerja_count: isSuccess ? 0 : 1,
      };
      return this.mixinSendTracker(TRACKER_EVENT_VP_PRAKERJA_CONFIRMATION, payload);
    },
    trackPrakerjaSuccess() {
      return this.mixinSendTracker(TRACKER_EVENT_VP_PRAKERJA_SUCCESS);
    },
    handleChangeSchedule() {
      if (this.isVoucherNotAvailable) return this.$router.back();

      if (CHANGE_CARD_NUMBER_ERROR_CODES.includes(this.error.pmoCode)) {
        this.sheetConfig.isErrorWithCtaActive = false;
        this.error.message = '';

        // user should re enter their prakerja number
        document.getElementById('prakerja-number-input').focus();
        window.scrollTo(0, 0);
        return;
      }

      this.$router.replace('/');
    },
    trackCheckoutVisit() {
      Braze.sendEvent('prakerja_checkout_page_visit', {
        product_name: this.getDetailCourse?.name,
        category: this.getDetailCourse?.category.name,
        provider: this.getDetailCourse?.deal.merchant.name,
        total_price: this.getDetailCourse?.discount.original_price,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.prakerja-checkout-form {
  &__error-message {
    &::v-deep {
      img {
        width: 264px;
      }
    }
  }

  &__sheet {
    &::v-deep {
      .bl-sheet__box {
        border-radius: 0;
      }
      .bl-sheet__nav-bar {
        display: none;
      }
    }
  }
}

.new-prakerja-checkout-form-image-item {
  &::v-deep {
    img {
      width: 64px;
      height: 64px;
    }
  }
}
</style>
